import { memo, useCallback } from 'react';
import type { CartLineItem } from '../../interfaces/Cart.model';
import { isEventProduct } from '../../utils/cart';
import { Spinner } from '../common/Spinner';

interface QuantityControlsProps {
    item: CartLineItem;
    isUpdating: boolean;
    itemError: string | undefined;
    onUpdateQuantity: (lineItemId: number, newQty: number) => Promise<void>;
    standalone: boolean;
}

export const QuantityControls = memo(
    ({ item, isUpdating, itemError, onUpdateQuantity, standalone }: QuantityControlsProps) => {
        const handleDecrease = useCallback(() => {
            if (onUpdateQuantity) {
                onUpdateQuantity(item.id, item.quantity - 1);
            }
        }, [item.id, item.quantity, onUpdateQuantity]);

        const handleIncrease = useCallback(() => {
            if (onUpdateQuantity) {
                onUpdateQuantity(item.id, item.quantity + 1);
            }
        }, [item.id, item.quantity, onUpdateQuantity]);

        const product = item.product;
        const isEventTicket = isEventProduct(product);
        const availableCapacity = isEventTicket ? product.availableCapacity : null;

        if (!standalone) {
            return (
                <div className="flex justify-start md:justify-center items-center space-x-2">
                    <span className="w-8 h-8 flex items-center justify-center bg-white">
                        {item.quantity}
                    </span>
                </div>
            );
        }

        return (
            <div className="flex justify-start md:justify-center items-center space-x-2">
                <button
                    type="button"
                    onClick={handleDecrease}
                    className="px-2 py-1 btn disabled:opacity-50 transition-opacity duration-200"
                    disabled={isUpdating || item.quantity <= 1}
                    aria-label="Decrease quantity"
                >
                    -
                </button>
                <div className="w-8 h-8 flex items-center justify-center bg-white">
                    <span
                        className={`transition-opacity duration-200 ${isUpdating ? 'opacity-0' : 'opacity-100'}`}
                    >
                        {item.quantity}
                    </span>
                    {isUpdating && (
                        <div className="absolute">
                            <Spinner color="green" />
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={handleIncrease}
                    className="px-2 py-1 btn disabled:opacity-50 transition-opacity duration-200"
                    disabled={
                        isUpdating ||
                        !!itemError ||
                        (availableCapacity !== null && item.quantity >= availableCapacity)
                    }
                    title={
                        availableCapacity !== null && item.quantity >= availableCapacity
                            ? 'No more tickets available'
                            : 'Increase quantity'
                    }
                    aria-label="Increase quantity"
                >
                    +
                </button>
            </div>
        );
    }
);

QuantityControls.displayName = 'QuantityControls';
